/*
 * Copyright (c) 2020 General Electric Company. All rights reserved.
 *
 * The copyright to the computer software herein is the property of
 * General Electric Company. The software may be used and/or copied only
 * with the written permission of General Electric Company or in accordance
 * with the terms and conditions stipulated in the agreement/contract
 * under which the software has been supplied.
 */

import * as _ from 'underscore';
import * as moment_ from 'moment';
import { MessageService } from '@core/lib/services';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ProxyDataService } from '@app/core/http/proxy-data.service';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { TranslateService } from '@ngx-translate/core';
import { LayoutService } from '@app/ui/common/layout.service';
import { SetItem } from '@wfm/virtual-grid';
import { DataTransformationService } from '@app/common/data-transformation.service';

@Injectable({
  providedIn: 'root'
})
export class WorkListHeaderDataService {
  /* constructor */
  constructor(
    private dataService: ProxyDataService,
    private messageService: MessageService,
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private translate: TranslateService,
    private layoutService: LayoutService,
    private dataTransformationService: DataTransformationService
  ) {
    this.broadcastHandlers();
  }

  /* Common Initialization */
  private selectedWorklistId = '';
  private todoWorklistId = '';
  private parentWorklistId: number = undefined;

  isSelectedWorklistFilterable = false;

  // Observers
  private filterHintsObserver$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  getFilterHintsObserver() {
    return this.filterHintsObserver$.asObservable();
  }

  private filterSortUpdatedObserver$: BehaviorSubject<{ reload: boolean }> = new BehaviorSubject({ reload: false });
  getFilterSortUpdatedObserver() {
    return this.filterSortUpdatedObserver$.asObservable();
  }

  // Promise
  private _preloadPromise: Promise<any> = undefined;
  private _worklistLoadPromise: Promise<any> = undefined;

  // Filter & Sort
  private filterParam: any = {};
  private sortParam: any = {};
  private filterCache = new Map();
  private sortingCache = new Map();
  private lastFilterRefreshed;
  private worklistStudyTimeDefnFilter;

  worklistStydyTimeRange: any = {};
  filterNeedToReset = false;
  worklistDefnFilter;
  searchList = false;
  isSavedList = false;
  isLinkedWL = false;

  // Data Model
  filterData = {
    acquisitionDate: {
      selectedItems: { selectedCode: 'Custom Range', selectedIndex: 16 },
      rangeLimits: { startDate: undefined, endDate: undefined, presets: undefined },
      reset: false,
      default: 16,
      fieldType: 'date',
      processing: false
    },
    patientDoB: {
      selectedItems: { selectedCode: 'Custom Range', selectedIndex: 16 },
      rangeLimits: { startDate: undefined, endDate: undefined, presets: undefined },
      reset: false,
      default: 16,
      fieldType: 'dob',
      processing: false
    },
    patientAge: {
      selectedItems: { selectedCode: '', selectedIndex: -1 },
      rangeLimits: { startDate: undefined, endDate: undefined, presets: undefined },
      reset: false,
      default: 0,
      fieldType: 'age',
      processing: false
    },
    timeOfExam: {
      selectedItems: { selectedCode: 'All Dates', selectedIndex: 0 },
      rangeLimits: { startDate: undefined, endDate: undefined, presets: undefined },
      reset: false,
      default: 0,
      fieldType: 'date',
      processing: false,
      specificData: 'studyTime',
      worklistSpecific: true
    },
    verificationTimeOfExam: {
      selectedItems: { selectedCode: 'All Dates', selectedIndex: 0 },
      rangeLimits: { startDate: undefined, endDate: undefined, presets: undefined },
      reset: false,
      default: 0,
      fieldType: 'date',
      processing: false,
      specificData: 'verifiedTimeOfExam',
      worklistSpecific: true
    },
    assignee: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'authors',
      specificData: 'assignee',
      worklistSpecific: false,
      processing: false,
      fieldType: 'name'
    },
    referringPhysician: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'referringPhysician',
      specificData: 'referringPhysician',
      worklistSpecific: false,
      processing: false,
      fieldType: 'name'
    },
    initialAuthor: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'authors',
      specificData: 'initialAuthor',
      worklistSpecific: false,
      processing: false,
      fieldType: 'name'
    },
    responsibleAuthor: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'authors',
      specificData: 'responsibleAuthor',
      worklistSpecific: false,
      processing: false,
      fieldType: 'name'
    },
    designatedAuthor: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'authors',
      specificData: 'designatedAuthor',
      worklistSpecific: false,
      processing: false,
      fieldType: 'name'
    },
    patientResidency: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'encounterClass',
      specificData: 'encounterClass',
      worklistSpecific: false,
      processing: false
    },
    departmentName: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'departments',
      specificData: 'departments',
      worklistSpecific: false,
      processing: false
    },
    modalityStationName: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'equipments',
      specificData: 'equipments',
      worklistSpecific: false,
      processing: false
    },
    site: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'sites',
      specificData: 'sites',
      worklistSpecific: false,
      processing: false
    },
    aiFinding: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'aiFinding',
      specificData: 'aiFinding',
      worklistSpecific: false,
      processing: false
    },
    verificationTicker: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'status',
      specificData: 'status',
      worklistSpecific: true,
      processing: false
    },
    orderingLocation: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'orderingLocation',
      specificData: 'orderingLocation',
      worklistSpecific: true,
      processing: false,
      fieldType: 'location'
    },
    modality: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'modality',
      specificData: 'modality',
      worklistSpecific: true,
      processing: false
    },
    status: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'status',
      specificData: 'status',
      worklistSpecific: true,
      processing: false
    },
    instituteName: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'instituteName',
      specificData: 'instituteName',
      worklistSpecific: true,
      processing: false
    },
    reportStatus: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'reportStatus',
      specificData: 'reportStatus',
      worklistSpecific: true,
      processing: false
    },
    imageCount: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'imageCount',
      specificData: 'imageCount',
      worklistSpecific: false,
      processing: false
    },
    priority: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'priority',
      specificData: 'priority',
      worklistSpecific: true,
      processing: false
    },
    exceptions: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'exceptions',
      specificData: 'exceptions',
      worklistSpecific: false,
      processing: false
    },
    gender: {
      items: [],
      selectedItems: [],
      disabled: false,
      baseData: 'patientGender',
      specificData: 'patientGender',
      worklistSpecific: false,
      processing: false
    }
  };

  // Age Filter Dropdown Template
  readonly agePresets = [
    { id: 'd', label: this.translate.instant('filtering.age.label.days') },
    { id: 'm', label: this.translate.instant('filtering.age.label.months') },
    { id: 'y', label: this.translate.instant('filtering.age.label.years') }
  ];
  getCalculatedDate(value: number, units: any) {
    let calDate = moment_();
    calDate = value < 0 ? calDate.add(Math.abs(value), units) : calDate.subtract(value, units);
    return calDate.toDate();
  }

  // Date Filter Dropdown Template
  readonly dateFormats = { dbFormat: 'YYYY-MM-DD', displayFormat: 'MM/DD/YYYY' };
  readonly datePresets = [
    {
      id: 'T+0',
      label: this.translate.instant('today'),
      getStartDate: () => {
        return moment_().startOf('day');
      },
      getEndDate: () => {
        return moment_().endOf('day');
      }
    },
    {
      id: 'T-1',
      label: this.translate.instant('yesterday'),
      getStartDate: () => {
        return moment_().startOf('day').subtract(1, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day').subtract(1, 'd');
      }
    },
    {
      id: 'T-2',
      label: this.translate.instant('last2days'),
      getStartDate: () => {
        return moment_().startOf('day').subtract(2, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day');
      }
    },
    {
      id: 'T-3',
      label: this.translate.instant('last3days'),
      getStartDate: () => {
        return moment_().startOf('day').subtract(3, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day');
      }
    },
    {
      id: 'T-7',
      label: this.translate.instant('last7days'),
      getStartDate: () => {
        return moment_().startOf('day').subtract(6, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day');
      }
    },
    {
      id: 'T-30',
      label: this.translate.instant('last30days'),
      getStartDate: () => {
        return moment_().startOf('day').subtract(30, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day');
      }
    },
    {
      id: 'T-365',
      label: this.translate.instant('last365days'),
      getStartDate: () => {
        return moment_().startOf('day').subtract(365, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day');
      }
    },
    {
      id: 'H-24',
      label: this.translate.instant('last24hours'),
      getStartDate: () => {
        return moment_().subtract(24, 'h');
      },
      getEndDate: () => {
        return moment_();
      }
    },
    {
      id: 'H-48',
      label: this.translate.instant('last48hours'),
      getStartDate: () => {
        return moment_().subtract(48, 'h');
      },
      getEndDate: () => {
        return moment_();
      }
    },
    {
      id: 'H-72',
      label: this.translate.instant('last72hours'),
      getStartDate: () => {
        return moment_().subtract(72, 'h');
      },
      getEndDate: () => {
        return moment_();
      }
    },
    {
      id: 'T+1',
      label: this.translate.instant('daterange.tomorrow'),
      getStartDate: () => {
        return moment_().startOf('day').add(1, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day').add(1, 'd');
      }
    },
    {
      id: 'T+2',
      label: this.translate.instant('daterange.next.2days'),
      getStartDate: () => {
        return moment_().startOf('day').add(1, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day').add(2, 'd');
      }
    },
    {
      id: 'T+3',
      label: this.translate.instant('daterange.next.3days'),
      getStartDate: () => {
        return moment_().startOf('day').add(1, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day').add(3, 'd');
      }
    },
    {
      id: 'T+7',
      label: this.translate.instant('daterange.next.7days'),
      getStartDate: () => {
        return moment_().startOf('day').add(1, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day').add(7, 'd');
      }
    },
    {
      id: 'T+30',
      label: this.translate.instant('daterange.next.30days'),
      getStartDate: () => {
        return moment_().startOf('day').add(1, 'd');
      },
      getEndDate: () => {
        return moment_().endOf('day').add(30, 'd');
      }
    }
  ];

  // Filter data access
  private formRequestFilterBodyItem(__filterName, __data) {
    const _tmpRequestBodyItem = { filterName: __filterName };
    if (__data) {
      _tmpRequestBodyItem['filterData'] = __data;
    }
    return _tmpRequestBodyItem;
  }

  getFilterDropdownHintData(_field): SetItem[] {
    // Get data from items if present else from base
    let baseData = [];
    if (this.filterData[_field]) {
      baseData =
        this.filterData[_field].items.length > 0
          ? this.filterData[_field].items
          : _.values(this.searchFilterCriteriaService.getFilterData(this.filterData[_field].baseData));
    }

    return baseData.map(item => {
      return {
        id: _field == 'assignee' ? item.userName : item.id,
        label: item.name
      };
    });
  }

  // broadcastHandlers Helper : Listener to reset for worklist loading done
  private resetWorklistLoadDoneHandler() {
    // Promise handler for worklist loaded
    this._worklistLoadPromise = new Promise(resolve => {
      const worklistLoadDoneListener = this.messageService.on('worklistLoaded.done').subscribe(() => {
        worklistLoadDoneListener.unsubscribe();
        resolve(true);
      });
    });
  }

  // broadcastHandlers
  private broadcastHandlers() {
    // Initialise worklist load handler
    this.resetWorklistLoadDoneHandler();

    // Promise handler for preload
    this._preloadPromise = new Promise(resolve => {
      const preloadSearchCriteriaDoneListener = this.messageService.on('preloadSearchCriteria.done').subscribe(() => {
        preloadSearchCriteriaDoneListener.unsubscribe();
        resolve(true);
      });
    });

    // handler for priorityCache.update
    this.messageService.on('priorityCache.update').subscribe(priorityList => {
      const items = this.filterData['priority'].items;
      const updatedPriorities = [];
      for (let i = 0; i < priorityList.length; i++) {
        for (let j = 0; j < items.length; j++) {
          if (Number(items[j].id) === priorityList[i].id) {
            items[j].name = priorityList[i].priorityName;
            items[j].priorityName = priorityList[i].priorityName;
            items[j].priorityBadgeColor = priorityList[i].priorityBadgeColor;
            items[j].examPriorityDesc = priorityList[i].priorityName;
            items[j].isPriorityEnabled = priorityList[i].isPriorityEnabled;
            updatedPriorities.push(items[j]);
          }
        }
      }
      this.filterData['priority'].items = updatedPriorities;
    });
  }

  // Load filter specific to worklist
  prefetch(filterParam, fields) {
    const _id = this.getWorklistId();

    // Check if Status & Status-Ticker filter for Linked Worklist
    if (this.isLinkedWL && fields.includes('verificationTicker')) {
      if (!fields.includes('status')) fields.push('status');
      fields.splice(fields.indexOf('verificationTicker'), 1);
    }

    const _filterRequest = {};
    const _filterLocal = {};
    _.each(fields, field => {
      // Determine whether to handle locally or delegate to API
      const _requestObj = {};
      let _tmpItem;

      // Have to remove check else filter call fails
      if (this.filterData[field] && field !== 'verificationTimeOfExam') {
        _requestObj['filterName'] = this.filterData[field]?.specificData;
        if (this.worklistDefnFilter && this.worklistDefnFilter[field] && !_.isEmpty(this.worklistDefnFilter[field])) {
          const _data = this.worklistDefnFilter[field];
          _requestObj['filterData'] = {};

          if (field === 'priority') {
            _.each(_data, key => {
              _tmpItem = this.searchFilterCriteriaService.getPriorityById(Number(key));
              if (_tmpItem) {
                _requestObj['filterData'][key] = {
                  id: key,
                  name: _tmpItem.name,
                  isPriorityEnabled: _tmpItem.isPriorityEnabled,
                  examPriorityDesc: _tmpItem.name
                };
              }
            });
            _requestObj['filterData'] = this.sortPriorityFilterData(_requestObj['filterData']);
          } else {
            _.each(_data, key => {
              _tmpItem = this.searchFilterCriteriaService.getFilterDataById(this.filterData[field].baseData, key);

              if (_tmpItem) {
                _requestObj['filterData'][key] = { id: key, name: _tmpItem.name };
              }
            });
          }
          _filterLocal[field] = _requestObj;
        } else if (_id !== -1 && this.filterData[field].worklistSpecific && !this.searchList && !this.isSavedList) {
          _filterRequest[field] = _requestObj;
        } else {
          _filterLocal[field] = _requestObj;
        }
      }
    });

    // Handle filter request & local
    if (!_.isEmpty(_filterRequest)) this.handleFilterRequest(_id, _filterRequest, filterParam);
    this.handleFilterLocal(_filterLocal, filterParam, true, _.isEmpty(_filterRequest));
  }

  // Request filter handler
  private handleFilterRequest(__id, __filterRequest, __filterParam) {
    if (!_.isEmpty(__filterRequest)) {
      this.dataService
        .create('worklists/' + __id + '/filters', { filterParamDTOList: _.values(__filterRequest) })
        .then(_filterResponse => {
          const _emptyFilterResponse = {};
          _.each(_filterResponse, (_fieldData, _fieldName) => {
            // Handle Date/Time field
            if (_.isEqual(_fieldName, this.filterData.timeOfExam.specificData)) {
              this.handleFilterDateTime(
                'timeOfExam',
                _fieldData.beginExamDateRange,
                _fieldData.endExamDateRange,
                _fieldData.examDateRangeToken
              );
            } else if (_.isEqual(_fieldName, this.filterData.verificationTimeOfExam.specificData)) {
              this.handleFilterDateTime(
                'verificationTimeOfExam',
                _fieldData.verifiedBeginDate,
                _fieldData.verifiedEndDate,
                _fieldData.verifiedDateRangeToken
              );
            }

            // HAndle empty response to handle local
            else if (_.isEmpty(_fieldData)) {
              _emptyFilterResponse[_fieldName] = this.formRequestFilterBodyItem(_fieldName, undefined);
            }
            // Load the data provided
            else {
              this.loadPrefetchData(
                _fieldName,
                this.updatePrefetchData(_fieldName, _fieldData),
                __filterParam,
                undefined
              );
            }
          });
          this.handleFilterLocal(_emptyFilterResponse, __filterParam, false, true);
        })
        .catch(_filterError => {
          this.handleFilterLocal(__filterRequest, __filterParam, false, true);
        });
    }
  }

  // Local filter handler
  private handleFilterLocal(__filterLocal, filterParam, __processData: boolean, __broadcastUpdate: boolean) {
    _.each(__filterLocal, (_filterData, _filterField) => {
      if (_filterData['filterData'] && __processData) {
        this.loadPrefetchData(_filterField, _filterData['filterData'], filterParam, undefined);
      } else {
        this.filterData[_filterField].items = [];
        this.filterData[_filterField].selectedItems = [];
      }
    });

    __broadcastUpdate && this.filterHintsObserver$.next(true);
  }

  // Study Time filter handler, Can improve further by moving the linked logic before requesting API.
  handleFilterDateTime(_attrId, _startDate, _endDate, _dateTimeToken) {
    if (_startDate || _endDate || _dateTimeToken) {
      // Update the filter definition if not present
      const isPreviousFilterAbsent =
        _.isEmpty(this.filterData[_attrId].rangeLimits.startDate) &&
        _.isEmpty(this.filterData[_attrId].rangeLimits.endDate) &&
        _.isEmpty(this.filterData[_attrId].rangeLimits.presets);

      if (isPreviousFilterAbsent) {
        this.filterData[_attrId].rangeLimits = this.processDateTimeFilter(
          _startDate && moment_(_startDate, this.dateFormats.displayFormat).format(this.dateFormats.dbFormat),
          _endDate && moment_(_endDate, this.dateFormats.displayFormat).format(this.dateFormats.dbFormat),
          _dateTimeToken
        );
      }
    }
  }

  sortPriorityFilterData(priorities) {
    let allPriorities = [];
    let disabledPriorities = [];
    _.each(priorities, item => {
      if (item.priorityEnable === true || item.isPriorityEnabled) {
        allPriorities.push(item);
      }
    });
    allPriorities = _.sortBy(allPriorities, 'examPriorityDesc');
    _.each(priorities, item => {
      if (item.priorityEnable === false || item.isPriorityEnabled === false) {
        disabledPriorities.push(item);
      }
    });
    disabledPriorities = _.sortBy(disabledPriorities, 'examPriorityDesc');
    _.each(disabledPriorities, item => {
      allPriorities.push(item);
    });
    return allPriorities;
  }

  // Update data
  updatePrefetchData(field, data) {
    if (data) {
      const isNameField = _.contains(
        ['referringPhysician', 'assignee', 'initialAuthor', 'responsibleAuthor', 'designatedAuthor', 'instituteName'],
        field
      );
      let _id, _name;
      const _deleteKeys = [];
      if (_.isEqual('priority', field)) {
        data = this.sortPriorityFilterData(data);
      }

      _.each(data, (item, key) => {
        _id = key;
        _name = item;

        if (isNameField) {
          _name = item['name'];
        } else if (_.isEqual('sites', field)) {
          // Need improvements, the key should be the parameter used to filter the worklist.
          _deleteKeys.push(key);
          key = item;
          _id = item;
        }

        if (_.isEqual('priority', field)) {
          data[key] = {
            id: item.examPriorityCode,
            name: item.examPriorityDesc,
            isPriorityEnabled: item.priorityEnable
          };
        } else {
          data[key] = { id: _id, name: _name };
        }
      });

      if (_.isEqual(field, 'orderingLocation') && data['0'] && _.isEqual(data['0']['name'], 'Unspecified')) {
        data['0']['name'] = this.translate.instant('workloadManager.listContainer.noInformationMsg');
      }

      _.each(_deleteKeys, key => {
        delete data[key];
      });
    }
    return data;
  }

  setWorklistDefnFilter(filter) {
    const parsedFilter = {};
    if (filter && filter.trim() !== '') {
      _.map(filter.split(';'), item => {
        if (item) {
          const tmp = item.split(':');
          parsedFilter[tmp[0]] = tmp[1].split(',');
        }
      });
    }
    return parsedFilter;
  }

  setWorklistDateTimeDefnFilter(filter) {
    if (filter) {
      // Study Date/Time
      this.filterData.timeOfExam.rangeLimits = this.processDateTimeFilter(
        filter.fromDate && filter.fromDate[0],
        filter.toDate && filter.toDate[0],
        filter.timeRange
      );

      // Verfification Date/Time
      this.filterData.verificationTimeOfExam.rangeLimits = this.processDateTimeFilter(
        filter.verifiedBeginDate && filter.verifiedBeginDate[0],
        filter.verifiedEndDate && filter.verifiedEndDate[0],
        filter.verifiedTimeRange
      );

      // Patient DoB
      this.filterData.patientDoB.rangeLimits = this.processDateTimeFilter(
        filter.dobFromDate && filter.dobFromDate[0],
        filter.dobToDate && filter.dobToDate[0],
        undefined
      );
    }
  }

  private processDateTimeFilter(_fromDate: string, _toDate: string, _dateRange: string) {
    const _field = { startDate: undefined, endDate: undefined, presets: undefined };

    if (_dateRange && (_dateRange[0] === 'H' || _dateRange[0] === 'T')) {
      const presetIndex = _.pluck(this.datePresets, 'id').indexOf(_dateRange);
      _field.startDate = this.datePresets[presetIndex].getStartDate();
      _field.endDate = this.datePresets[presetIndex].getEndDate();
    } else if (_toDate && _fromDate) {
      _field.startDate = moment_(_fromDate).startOf('day');
      _field.endDate = moment_(_toDate).endOf('day');
    }

    if (_field.startDate && _field.endDate) {
      _field.presets = this.datePresets.filter(preset => {
        return (
          _field.startDate.isSameOrBefore(preset.getStartDate(), 'day') &&
          _field.endDate.isSameOrAfter(preset.getEndDate(), 'day')
        );
      });
    }

    return _field;
  }

  /* Parsing & Updating the Inline filter Data */
  loadPrefetchData(columnId: string, data, filterParam, mode) {
    const selectionMap = this.getSelectionMap(columnId, data, filterParam, mode);
    this.filterData[columnId].items = selectionMap.items;
    if (columnId === 'status') {
      this.filterData['verificationTicker'].items = selectionMap.items;
    }

    if (
      _.isEmpty(this.filterData[columnId].items) ||
      (!_.isEmpty(this.filterData[columnId].items) && this.filterData[columnId].items[0].id === 'Not Available')
    ) {
      this.filterData[columnId].disabled = true;
    } else {
      this.filterData[columnId].disabled = false;
      this.filterData[columnId].selectedItems = selectionMap.selected;
    }
    this.filterData[columnId].processing = false;
  }
  getSelectionMap(columnId, data, filterParam, mode) {
    const selectionMap = { items: [], selected: [], filtered: [] };
    if (data && filterParam[columnId] && filterParam[columnId].length > 0) {
      if (columnId === 'priority') {
        _.each(filterParam[columnId], item => {
          const itemIndex = _.findIndex(data, { id: item.id });
          if (data[itemIndex]) {
            data[itemIndex].selected = true;
            selectionMap.selected.push(data[itemIndex]);
          }
        });
      } else {
        _.each(filterParam[columnId], item => {
          if (data[item.id]) {
            data[item.id].selected = true;
            selectionMap.selected.push(data[item.id]);
          }
        });
      }
    }

    selectionMap.items = _.isEqual(mode, 'search')
      ? data
      : columnId === 'priority'
      ? _.values(data)
      : _.sortBy(_.values(data), 'name');
    return selectionMap;
  }

  /* Setters / Getters */
  getSelectedWorklistId() {
    return this.selectedWorklistId;
  }
  getWorklistId() {
    let id: number;
    if (isNaN(parseInt(this.selectedWorklistId))) {
      id = isNaN(parseInt(this.todoWorklistId)) ? -1 : parseInt(this.todoWorklistId);
    } else {
      id = this.parentWorklistId ? this.parentWorklistId : parseInt(this.selectedWorklistId);
    }
    return id;
  }
  getAuthor(id) {
    const auth = this.searchFilterCriteriaService.getAuthor(id);
    return auth ? auth['name'] : undefined;
  }

  // Initialize
  initializeWorklistChange(list: any) {
    const differentWorkList = !_.isEqual(list.id, this.selectedWorklistId);
    this.selectedWorklistId = list.id;
    this.searchList = list.isSearchList;
    this.isSavedList = list.isSavedList;
    this.isLinkedWL = list.isLinked;
    this.todoWorklistId = isNaN(parseInt(list.id)) ? list.worklistId : list.id;
    this.parentWorklistId = list.parentId && parseInt(list.parentId) > 0 ? parseInt(list.parentId) : undefined;
    this.worklistDefnFilter = this.setWorklistDefnFilter(list.filterJson);
    this.setWorklistDateTimeDefnFilter(this.worklistDefnFilter);
    this.isSelectedWorklistFilterable = list.isFilterable;
    if (list.isFilterable) {
      if (differentWorkList || this.searchList) {
        this.resetWorklistLoadDoneHandler();
        this.resetFilterParam(this.selectedWorklistId);
        this.resetSortParam(this.selectedWorklistId);
      } else {
        const reload = !_.isEqual(this.layoutService.getLayoutFilterAttributeListFromCache(), this.lastFilterRefreshed);
        if (reload) {
          this.prefetchFilters();
        }
      }
    }

    this.filterSortUpdatedObserver$.next({ reload: false });
  }

  // Retrieve
  getParsedFilterParam() {
    const selectedFilterParams = {};
    const timeRangeOpt = { 'H-24': 24, 'H-48': 48, 'H-72': 72 };
    if (!_.isEmpty(this.filterParam)) {
      for (const key in this.filterParam) {
        if (this.filterParam.hasOwnProperty(key) && !_.isEmpty(this.filterParam[key])) {
          const attr = this.layoutService.getAttributeFromCache(key);
          if (attr.dbParamName === 'nhi') {
            this.filterParam['nhi'][0].id = this.dataTransformationService.getNonFormattedNHI(
              this.filterParam['nhi'][0].id
            );
          }
          if (attr.displayType === 'date') {
            if (this.filterParam[key][0].selectionId in timeRangeOpt && attr.dbParamName === 'timeOfExam') {
              selectedFilterParams['timeRange'] = timeRangeOpt[this.filterParam[key][0].selectionId];
            } else if (
              this.filterParam[key][0].selectionId in timeRangeOpt &&
              attr.dbParamName === 'verifiedTimeOfExam'
            ) {
              selectedFilterParams['verifiedTimeRange'] = timeRangeOpt[this.filterParam[key][0].selectionId];
            } else {
              selectedFilterParams[attr.oldestDate] = moment_(this.filterParam[key][0].startDate).format(
                this.dateFormats.dbFormat
              );
              selectedFilterParams[attr.newestDate] = moment_(this.filterParam[key][0].endDate).format(
                this.dateFormats.dbFormat
              );
            }
          } else if (attr.displayType === 'age') {
            selectedFilterParams[attr.oldestDate] = moment_(this.filterParam[key][0].startDate).format(
              this.dateFormats.dbFormat
            );
            selectedFilterParams[attr.newestDate] = moment_(this.filterParam[key][0].endDate).format(
              this.dateFormats.dbFormat
            );
          } else {
            selectedFilterParams[attr.dbParamName] = _.pluck(this.filterParam[key], 'id');
          }
        }
      }
    }
    return selectedFilterParams;
  }
  getParsedSortParam() {
    const selectedSortParam = { item: {} };
    if (!_.isEmpty(this.sortParam)) {
      const sortParameters = this.sortParam;
      let columnName, sortOrder;
      _.each(sortParameters, (sortColOrder, sortColId) => {
        if (!_.isEmpty(sortColOrder) && (_.isEqual(sortColOrder, 'UP') || _.isEqual(sortColOrder, 'DOWN'))) {
          columnName = this.layoutService.getAttributeFromCache(sortColId).dbParamName;
          if (_.isEqual(sortColId, 'priority') || _.isEqual(sortColId, 'aiFinding')) {
            sortOrder = _.isEqual(sortColOrder, 'UP') ? 'DESC' : 'ASC';
          } else {
            sortOrder = _.isEqual(sortColOrder, 'UP') ? 'ASC' : 'DESC';
          }

          selectedSortParam.item[columnName] = sortOrder;
        }
      });
    }
    return selectedSortParam;
  }

  getWorklistDefnFilter() {
    return this.worklistDefnFilter;
  }
  getWorklistStudyTimeDefnFilter() {
    return this.worklistStudyTimeDefnFilter;
  }

  // Manage Filter & Sort
  applyFilter(parsedFilterVal: any, originalFilterVal: Array<any>) {
    this.filterParam = parsedFilterVal;
    this.saveFilterToCache(this.selectedWorklistId, parsedFilterVal, originalFilterVal);
  }

  applySort(parsedSortVal: any, originalSortVal: Array<any>) {
    this.sortParam = parsedSortVal;
    this.saveSortToCache(this.selectedWorklistId, parsedSortVal, originalSortVal);
  }

  // Remove
  deleteFilterAndSort(id: string, forceRefresh = true) {
    this.removeFilterParam(id);
    this.removeSortParam(id);
    if (id === this.selectedWorklistId) this.filterSortUpdatedObserver$.next({ reload: forceRefresh });
  }
  deleteFilter(id: string, forceRefresh = true) {
    this.removeFilterParam(id);
    if (id === this.selectedWorklistId) this.filterSortUpdatedObserver$.next({ reload: forceRefresh });
  }
  deleteSort(id: string, forceRefresh = true) {
    this.removeSortParam(id);
    if (id === this.selectedWorklistId) this.filterSortUpdatedObserver$.next({ reload: forceRefresh });
  }

  // Filter Cache
  private removeFilterParam(id: string) {
    this.filterCache.delete(id);
    if (id === this.selectedWorklistId) this.filterParam = {};
  }
  private saveFilterToCache(worklistId: string, parsedData: any, originalData: any) {
    this.filterCache.set(worklistId, { parsed: parsedData, original: originalData });
  }
  getFilterParamFromCache(id: string, isOriginal: boolean) {
    const dataType: string = isOriginal ? 'original' : 'parsed';
    const cacheFilter = this.filterCache.get(id);
    return cacheFilter && cacheFilter[dataType] ? cacheFilter[dataType] : isOriginal ? [] : {};
  }

  // Sorting Cache
  private removeSortParam(id: string) {
    this.sortingCache.delete(id);
    if (id === this.selectedWorklistId) this.sortParam = {};
  }
  private saveSortToCache(worklistId: string, parsedData: any, originalData: any) {
    this.sortingCache.set(worklistId, { parsed: parsedData, original: originalData });
  }
  getSortParamFromCache(id: string, isOriginal: boolean) {
    const dataType: string = isOriginal ? 'original' : 'parsed';
    const cacheSort = this.sortingCache.get(id);
    return cacheSort && cacheSort[dataType] ? cacheSort[dataType] : isOriginal ? [] : {};
  }

  // Reset
  private resetSortParam(id) {
    if (this.searchList) this.removeSortParam(id);
    this.sortParam = this.getSortParamFromCache(id, false);
  }
  resetFilterParam(id) {
    if (this.searchList) this.removeFilterParam(id);
    this.filterNeedToReset = false;
    this.filterParam = this.getFilterParamFromCache(id, false);
    this.prefetchFilters();
  }

  // Reset inline filter dropdown lookups based on layout
  prefetchFilters() {
    const filterParam = this.filterParam;
    Promise.all([this._preloadPromise, this._worklistLoadPromise]).then(() => {
      const list = this.layoutService.getLayoutFilterAttributeListFromCacheNames();
      list && list.push('timeOfExam');
      if (list && list.length > 1) this.prefetch(filterParam, list);
    });
  }
  checkFilterNeedToReset() {
    return this.filterNeedToReset;
  }
}
